/**
 * Shared helpers for WhatsApp OTP proposal approval (customer-facing).
 * Phone identity is canonical E.164 (e.g. +6591234567). Legacy Singapore-only
 * 8-digit storage remains readable during the compatibility period.
 *
 * Clarity One login continues to use Singapore-only `normalisePhone` in phoneUtils —
 * do not reuse that authority for proposal-approval OTP.
 */
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { normalizeCustomerEmail } from "./customerEmailAddresses";

export const DEFAULT_APPROVAL_WHATSAPP_COUNTRY: CountryCode = "SG";

/** Enough for canonical E.164 including leading '+'. */
export const PROPOSAL_APPROVAL_PHONE_NORMALIZED_MAX_LENGTH = 20;

const REGION_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

function isReasonablyValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isApprovalWhatsAppCountryCode(value: string | null | undefined): value is CountryCode {
  const code = String(value ?? "")
    .trim()
    .toUpperCase();
  if (code.length !== 2) return false;
  return (getCountries() as string[]).includes(code);
}

export function resolveApprovalWhatsAppCountry(
  value: string | null | undefined,
): CountryCode {
  if (isApprovalWhatsAppCountryCode(value)) return value.trim().toUpperCase() as CountryCode;
  return DEFAULT_APPROVAL_WHATSAPP_COUNTRY;
}

export function approvalWhatsAppCountryLabel(country: CountryCode): string {
  const name = REGION_NAMES?.of(country) ?? country;
  const calling = getCountryCallingCode(country);
  return `${name} (+${calling})`;
}

/** Curated + full ISO list for the approval country selector (SG first). */
export function listApprovalWhatsAppCountries(): Array<{
  code: CountryCode;
  label: string;
  callingCode: string;
}> {
  const preferred: CountryCode[] = ["SG", "MY", "HK", "GB", "US", "AU", "CN", "ID", "IN", "JP", "KR", "TH", "PH", "AE", "NZ", "CA"];
  const all = getCountries();
  const seen = new Set<string>();
  const rows: Array<{ code: CountryCode; label: string; callingCode: string }> = [];

  for (const code of [...preferred, ...all]) {
    if (seen.has(code)) continue;
    seen.add(code);
    rows.push({
      code,
      label: approvalWhatsAppCountryLabel(code),
      callingCode: getCountryCallingCode(code),
    });
  }
  return rows;
}

function parseApprovalPhoneCandidate(
  raw: string,
  selectedCountry: CountryCode,
): ReturnType<typeof parsePhoneNumberFromString> {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith("+")) {
    return parsePhoneNumberFromString(trimmed);
  }

  // National / local input — interpret using the selected country only.
  return parsePhoneNumberFromString(trimmed, selectedCountry);
}

/**
 * Resolve selected country + entered number into canonical E.164 for proposal approval.
 * Does not silently reinterpret an explicit foreign number as Singapore.
 */
export function resolveApprovalWhatsApp(
  raw: string | null | undefined,
  countryCode: string | null | undefined = DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
):
  | {
      ok: true;
      phoneNormalized: string;
      phoneDisplay: string;
      country: CountryCode;
      countryCallingCode: string;
    }
  | { ok: false; message: string } {
  const selected = resolveApprovalWhatsAppCountry(countryCode);
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return {
      ok: false,
      message: "Please enter the WhatsApp number where you would like to receive the verification code.",
    };
  }

  const parsed = parseApprovalPhoneCandidate(trimmed, selected);
  if (!parsed || !parsed.isValid()) {
    return {
      ok: false,
      message: `Please enter a valid WhatsApp number for ${approvalWhatsAppCountryLabel(selected)}.`,
    };
  }

  const resolvedCountry = (parsed.country ?? selected) as CountryCode;
  const selectedCalling = getCountryCallingCode(selected);
  if (parsed.countryCallingCode !== selectedCalling || (parsed.country && parsed.country !== selected)) {
    const actualLabel = parsed.country
      ? approvalWhatsAppCountryLabel(parsed.country)
      : `+${parsed.countryCallingCode}`;
    return {
      ok: false,
      message: `This number belongs to ${actualLabel}. Select that country, or enter a number for ${approvalWhatsAppCountryLabel(selected)}.`,
    };
  }

  const phoneNormalized = parsed.format("E.164");
  if (phoneNormalized.length > PROPOSAL_APPROVAL_PHONE_NORMALIZED_MAX_LENGTH) {
    return {
      ok: false,
      message: "That WhatsApp number is too long. Please check it and try again.",
    };
  }

  return {
    ok: true,
    phoneNormalized,
    phoneDisplay: parsed.formatInternational(),
    country: resolvedCountry,
    countryCallingCode: parsed.countryCallingCode,
  };
}

/**
 * Identity keys for OTP lookup / rate-limit matching during the SG legacy period.
 * New writes always use E.164; historical Singapore rows may be 8-digit local.
 */
export function proposalApprovalPhoneIdentityKeys(phoneNormalized: string | null | undefined): string[] {
  const raw = String(phoneNormalized ?? "").trim();
  if (!raw) return [];

  const keys = new Set<string>();
  keys.add(raw);

  if (/^\d{8}$/.test(raw)) {
    keys.add(`+65${raw}`);
  }

  const e164Candidate = raw.startsWith("+") ? raw : raw.startsWith("65") && raw.length === 10 ? `+${raw}` : null;
  if (e164Candidate) {
    keys.add(e164Candidate);
    const parsed = parsePhoneNumberFromString(e164Candidate);
    if (parsed?.isValid()) {
      keys.add(parsed.format("E.164"));
      if (parsed.country === "SG" && /^\d{8}$/.test(parsed.nationalNumber)) {
        keys.add(parsed.nationalNumber);
      }
    }
  } else if (/^\+65\d{8}$/.test(raw)) {
    keys.add(raw.slice(3));
  }

  return Array.from(keys);
}

export function proposalApprovalPhonesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = proposalApprovalPhoneIdentityKeys(a);
  const right = new Set(proposalApprovalPhoneIdentityKeys(b));
  return left.some((key) => right.has(key));
}

/** Twilio / WhatsApp destination must be a single E.164 value (no double country prefix). */
export function toProposalApprovalWhatsAppDestination(phoneNormalized: string): string {
  const keys = proposalApprovalPhoneIdentityKeys(phoneNormalized);
  const e164 = keys.find((key) => key.startsWith("+")) ?? phoneNormalized;
  const digits = e164.replace(/\D/g, "");
  if (!digits) return e164.startsWith("+") ? e164 : `+${e164}`;
  return `+${digits}`;
}

export function displayWhatsAppFromNormalized(normalized: string): string {
  const raw = String(normalized ?? "").trim();
  if (!raw) return "";

  if (/^\d{8}$/.test(raw)) {
    return `+65 ${raw.slice(0, 4)} ${raw.slice(4)}`;
  }

  const e164 = raw.startsWith("+") ? raw : `+${raw.replace(/\D/g, "")}`;
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed?.isValid()) return parsed.formatInternational();
  return e164;
}

/**
 * Mask for timelines / OTP UI — keep country code + limited national digits only.
 * Never expose the full national number.
 */
export function maskWhatsAppDisplay(displayOrNormalized: string): string {
  const raw = String(displayOrNormalized ?? "").trim();
  if (!raw) return "•••• ••••";

  if (/^\d{8}$/.test(raw)) {
    return `+65 ${raw.slice(0, 2)}•• ••${raw.slice(6)}`;
  }

  const digitsOnly = raw.replace(/\D/g, "");
  if (digitsOnly.length === 10 && digitsOnly.startsWith("65")) {
    const local = digitsOnly.slice(2);
    return `+65 ${local.slice(0, 2)}•• ••${local.slice(6)}`;
  }

  const e164 = raw.startsWith("+") ? raw : digitsOnly ? `+${digitsOnly}` : raw;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed?.isValid()) {
    return "•••• ••••";
  }

  const national = parsed.nationalNumber;
  const cc = parsed.countryCallingCode;
  if (national.length <= 4) {
    return `+${cc} ${"•".repeat(Math.max(2, national.length))}`;
  }
  const head = national.slice(0, 2);
  const tail = national.slice(-2);
  const middleLen = Math.max(2, national.length - 4);
  const middle = "•".repeat(middleLen);
  // Keep a readable SG-style grouping when possible; otherwise one compact mask.
  if (parsed.country === "SG" && national.length === 8) {
    return `+${cc} ${head}•• ••${tail}`;
  }
  return `+${cc} ${head}${middle}${tail}`;
}

export function resolveOptionalApprovalEmail(
  raw: string | null | undefined,
): { ok: true; email: string | null } | { ok: false; message: string } {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { ok: true, email: null };
  if (!isReasonablyValidEmail(trimmed)) {
    return { ok: false, message: "That does not look like a valid email. Please check it, or leave it blank." };
  }
  return { ok: true, email: normalizeCustomerEmail(trimmed) };
}

export function resolveRequiredApproverName(
  raw: string | null | undefined,
): { ok: true; name: string } | { ok: false; message: string } {
  const name = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!name) {
    return { ok: false, message: "Please share your name so we can record your approval." };
  }
  if (name.length > 255) {
    return { ok: false, message: "That name is a little too long. Please shorten it." };
  }
  return { ok: true, name };
}

export function buildProposalApprovalAuditReference(proposalId: number, approvedAt: Date): string {
  const stamp = approvedAt.toISOString().replace(/[-:TZ.]/g, "").slice(0, 14);
  return `PA-${proposalId}-${stamp}`;
}

/** Seed country + national input from a stored customer contact when possible. */
export function seedApprovalWhatsAppFromContact(contact: string | null | undefined): {
  country: CountryCode;
  number: string;
} {
  const trimmed = String(contact ?? "").trim();
  if (!trimmed) {
    return { country: DEFAULT_APPROVAL_WHATSAPP_COUNTRY, number: "" };
  }
  const parsed = parsePhoneNumberFromString(
    trimmed.startsWith("+") ? trimmed : trimmed,
    DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
  );
  if (parsed?.isValid() && parsed.country) {
    return {
      country: parsed.country,
      number: parsed.formatNational(),
    };
  }
  // Legacy bare SG local
  if (/^\d{8}$/.test(trimmed.replace(/\D/g, "")) && !trimmed.includes("+")) {
    const digits = trimmed.replace(/\D/g, "");
    return { country: "SG", number: `${digits.slice(0, 4)} ${digits.slice(4)}` };
  }
  return { country: DEFAULT_APPROVAL_WHATSAPP_COUNTRY, number: trimmed };
}
